import {useConfig} from '@aboutyou/config';
import {
  MissionDetail_MissionBadge_BadgeStyle,
  MissionDetail_MissionBottomSheet,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/mission.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  ContentPopper,
  Flyout,
  GenericIconName,
  Icon,
  Image,
  ModalDialog,
  ModalDialogBodyContent,
  PrimaryContentPopperButton,
  SPACING,
  SecondaryContentPopperButton,
  Text,
  Z_INDEXES,
  mobileOnlyLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {FramedMissionBadge} from './FramedMissionBadge/FramedMissionBadge';

const FRAMED_BADGE_SIZE = 88;

export const CoinsMissionInfo: React.FC<
  MissionDetail_MissionBottomSheet & {
    isOpen: boolean;
    onClose: VoidFunction;
  }
> = ({isOpen, onClose, badge, summary, cta}) => {
  const {device} = useConfig();
  const {t} = useTranslation();

  return (
    <StyledContentPopper
      data-testid="coinsMissionInfo"
      renderInPortal
      contentFitWidth
      LayoutComponent={device === 'desktop' ? ModalDialog : Flyout}
      onClose={onClose}
      open={isOpen}
      bodyContent={
        <Wrapper>
          <HeaderContent>
            {badge.style === MissionDetail_MissionBadge_BadgeStyle.FRAMED ? (
              <FramedMissionBadge
                image={badge.image}
                size={FRAMED_BADGE_SIZE}
              />
            ) : (
              <Image
                src={badge.image.url}
                breakpoints={[{width: '88px'}]}
                ratio="1:1"
                widths={[88, 176, 352]}
              />
            )}
            <div>
              <Text size="px20" weight="mediumHeavy">
                {badge.label}
              </Text>

              <Icon asset={GenericIconName.CoinsDoubleColor} size="S" />
            </div>
          </HeaderContent>
          <BodyContent>
            <Text size="px20" weight="mediumHeavy">
              {summary.title}
            </Text>
            <Text size="px14" color="Text.Grey" weight="mediumBold">
              {summary.description}
            </Text>
          </BodyContent>
        </Wrapper>
      }
      footerContent={
        <>
          <SecondaryContentPopperButton
            data-testid="coinsMissionInfoCloseButton"
            label={t('Close')}
            onClick={onClose}
          />
          {cta && (
            <StyledPrimaryContentPopperButton
              data-testid="coinsMissionInfoCTAButton"
              href={cta.linkTarget.url}
              label={cta.label}
            />
          )}
        </>
      }
    />
  );
};

const StyledContentPopper = styled(ContentPopper)`
  width: 100%;
  height: 100%;

  ${mobileOnlyLinaria(`
    z-index: calc(${Z_INDEXES.MODAL_DIALOG_WRAPPER} + 1);
  `)}

  ${ModalDialogBodyContent} {
    width: clamp(375px, 20vw, 500px);
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING['Y.Xl']};
  padding-bottom: ${SPACING['Y.Md']};
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING['Y.Sm']};

  > img {
    width: 88px;
    height: 88px;
    flex: 0 0 88px;
    padding: 3px;
  }

  > div {
    display: flex;
    align-items: center;
    gap: ${SPACING['X.Sm']};
  }
`;

const BodyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${SPACING['Y.Xs']};

  > span {
    &:first-child {
      line-height: 1.2;
    }

    &:last-child {
      line-height: 1.5;
    }
  }
`;

const StyledPrimaryContentPopperButton = styled(PrimaryContentPopperButton)`
  white-space: nowrap;
`;
